import { supabase } from "@/integrations/supabase/client";
import { BADGE_DEFINITIONS } from "./badges";

interface BadgeCheckContext {
  userId: string;
  totalSimulations: number;
  totalCoachingSessions: number;
  goalsReached: number;
  highestScore: number;
  highestReadinessScore: number;
  currentStreak: number;
  totalCpdPoints: number;
}

interface BadgeAwardOptions {
  cpdEnabled?: boolean;
}

export async function checkAndAwardBadges(
  context: BadgeCheckContext,
  { cpdEnabled = true }: BadgeAwardOptions = {},
) {
  const earnedBadges: string[] = [];

  // Get already earned badges
  const { data: existingBadges } = await supabase
    .from('user_badges')
    .select('badge_id')
    .eq('user_id', context.userId);

  const alreadyEarned = new Set(existingBadges?.map(b => b.badge_id) || []);

  // Check each badge criteria
  for (const badge of BADGE_DEFINITIONS) {
    if (alreadyEarned.has(badge.id)) continue;

    let earned = false;

    switch (badge.criteria.type) {
      case 'simulations_completed':
        earned = context.totalSimulations >= (badge.criteria.count || 0);
        break;
      
      case 'coaching_completed':
        earned = context.totalCoachingSessions >= (badge.criteria.count || 0);
        break;
      
      case 'goals_reached':
        earned = context.goalsReached >= (badge.criteria.count || 0);
        break;
      
      case 'score_threshold':
        earned = context.highestScore >= (badge.criteria.score || 0);
        break;
      
      case 'readiness_threshold':
        earned = context.highestReadinessScore >= (badge.criteria.score || 0);
        break;
      
      case 'perfect_score':
        earned = context.highestScore === 100;
        break;
      
      case 'streak':
        earned = context.currentStreak >= (badge.criteria.days || 0);
        break;
      
      case 'cpd_points':
        earned = cpdEnabled && context.totalCpdPoints >= (badge.criteria.points || 0);
        break;
    }

    if (earned) {
      // Award badge
      const { error } = await supabase
        .from('user_badges')
        .insert({
          user_id: context.userId,
          badge_id: badge.id,
          badge_name: badge.name,
          badge_icon: badge.icon,
          badge_description: badge.description,
        });

      if (!error) {
        earnedBadges.push(badge.id);

        // Log activity
        await supabase
          .from('user_activity_log')
          .insert({
            user_id: context.userId,
            activity_type: 'badge_earned',
            title: `🎉 Badge Earned: ${badge.name}`,
            description: badge.description,
            metadata: { badge_id: badge.id, badge_icon: badge.icon },
          });

        if (cpdEnabled) {
          await supabase
            .from('user_cpd_points')
            .insert({
              user_id: context.userId,
              points: 2,
              source: 'badge_earned',
              description: `Earned badge: ${badge.name}`,
            });
        }
      }
    }
  }

  return earnedBadges;
}
